import PropTypes from "prop-types";
import {
  CardPreview,
  Content,
  Header,
  Text,
  ActionMenu,
  MenuSection,
  MenuItem,
  ActionButton,
  ProgressCircle,
} from "@react-spectrum/s2";
import LogoIcon from "@react-spectrum/s2/icons/Logo";
import ImageIcon from "@react-spectrum/s2/icons/Image";
import DeleteIcon from "@react-spectrum/s2/icons/Delete";
import DragHandleIcon from "icon:@app/assets/DragHandle.svg";

import { S3Image } from "@app/components/S3Image";
import { HoverState } from "@app/components/HoverState";
import { FileType } from "@app/components/FileType";

import {
  style,
  iconStyle,
} from "@react-spectrum/s2/style" with { type: "macro" };

const AssetActionMenu = ({
  item = {},
  templateData,
  onMenuAction,
  ...props
}) => {
  return (
    <ActionMenu onAction={(action) => onMenuAction(action, item)} {...props}>
      {!!item?.isDisabled && (
        <Header>
          <Text slot="description">Low‑resolution image</Text>
        </Header>
      )}
      <MenuSection>
        {templateData?.assets
          .filter((asset) => asset.type === "image")
          .map((asset) => (
            <MenuItem
              key={asset.name}
              id={asset.name}
              aria-label={`Use as ${(asset?.label ?? asset.name).toLowerCase()}`}
            >
              {["Logo", "App-icon"].includes(asset.name) ? (
                <LogoIcon />
              ) : (
                <ImageIcon />
              )}
              <Text>{`Use as ${(asset?.label ?? asset.name).toLowerCase()}`}</Text>
            </MenuItem>
          ))}
      </MenuSection>
      {!!item?.filePath && (
        <>
          <MenuSection>
            <MenuItem
              id="delete"
              aria-label="Delete asset"
              UNSAFE_className={style({ color: "negative" })}
            >
              <Text
                styles={style({
                  display: "inline-flex",
                  // alignItems: "center",
                  color: "negative",
                })}
              >
                <DeleteIcon />
                <Text slot={null} styles={style({ marginTop: 2 })}>
                  Delete
                </Text>
              </Text>
            </MenuItem>
          </MenuSection>
        </>
      )}
    </ActionMenu>
  );
};

AssetActionMenu.propTypes = {
  item: PropTypes.object,
  templateData: PropTypes.object,
  onMenuAction: PropTypes.func,
};

export const AssetCard = (props) => {
  const { item = {}, templateData, onMenuAction, children } = props;

  return (
    <>
      <CardPreview>
        <S3Image
          filePath={item?.filePath}
          src={(item?.thumb ?? item?.src) || ""}
          alt={item?.alt || item.id}
          styles={style({
            objectFit: "contain",
            size: "full",
            opacity: {
              isDisabled: 0.25,
              default: 1,
            },
          })({ isDisabled: item.isDisabled })}
        />
        {!!item?.isLoading && (
          <ProgressCircle
            aria-label="Loading"
            isIndeterminate
            staticColor="auto"
            styles={style({
              position: "absolute",
              top: "50%",
              insetStart: "50%",
            })}
            UNSAFE_className={style({
              transform: "translate(-50%, -50%)",
            })}
          />
        )}
        <div
          className={style({
            display: {
              default: "unset",
              md: "none",
            },
            position: "absolute",
            inset: 0,
          })}
        >
          <AssetActionMenu
            item={item}
            templateData={templateData}
            onMenuAction={onMenuAction}
            isQuiet={false}
            staticColor="auto"
            styles={style({ position: "absolute", top: 28, insetEnd: 8 })}
          />
        </div>
      </CardPreview>
      <HoverState
        showOutline={false}
        isTinted
        renderBehavior="always"
        styles={style({
          display: {
            default: "none",
            md: "unset",
          },
        })}
      >
        <Text
          slot="title"
          styles={style({
            font: "ui-sm",
            color: "neutral-subdued",
            position: "absolute",
            bottom: 8,
            insetStart: 12,
          })}
        >
          {`${Math.round(item?.width ?? item?.size?.width ?? 0)} × ${Math.round(item?.height ?? item?.size?.height ?? 0)} px`}
        </Text>
        {children}
        {typeof onMenuAction === "function" && (
          <AssetActionMenu
            item={item}
            templateData={templateData}
            onMenuAction={onMenuAction}
            isQuiet={false}
            styles={style({ position: "absolute", top: 28, insetEnd: 8 })}
          />
        )}
        <ActionButton
          slot="drag"
          isQuiet
          size="XS"
          styles={style({
            position: "absolute",
            top: 4,
            insetStart: 4,
            cursor: "grab",
            visibility: {
              isDisabled: "hidden",
              default: "visible",
            },
          })({ isDisabled: item.isDisabled })}
        >
          <DragHandleIcon UNSAFE_style={{ width: 10, height: 10 }} />
        </ActionButton>
        {!!item?.isDisabled && (
          <Text
            styles={style({
              font: "ui-sm",
              position: "absolute",
              top: 8,
              insetStart: 12,
            })}
          >
            Low‑res image
          </Text>
        )}
      </HoverState>
    </>
  );
};

AssetCard.propTypes = {
  item: PropTypes.object,
  templateData: PropTypes.object,
  onMenuAction: PropTypes.func,
  children: PropTypes.node,
};
